import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { useForm } from "react-hook-form"
import type { UseFormReturn } from "react-hook-form"

import { useCreateCustomerPurchaseOrder } from "./useCreateCustomerPurchaseOrder"

import type { CustomerPurchaseOrder } from "@/graphql/codegen/graphql"
import { type CustomerPurchaseOrderFormValues, customerPurchaseOrderFormSchema } from "@/screens/CustomerOrders/schemas"
import { toCustomerPurchaseOrderCreateInput } from "@/screens/CustomerOrders/utils"

interface UseCustomerPurchaseOrderFormProps {
  companyId: string
  shipToLocationId: string
  onClose: () => void
  onDuplicateSelect: (cpoNumber: string) => void
  onSubmit: () => void
}

interface UseCustomerPurchaseOrderFormReturn {
  methods: UseFormReturn<CustomerPurchaseOrderFormValues>
  handleSubmit: () => void
  handleCancel: () => void
  canSubmit: boolean
  isDuplicate: boolean
  duplicateCpo: CustomerPurchaseOrder | undefined
  handleCreateNew: () => void
  handleViewEditExisting: () => void
  isCreating: boolean
  isCancelConfirmationOpen: boolean
  setIsCancelConfirmationOpen: (isOpen: boolean) => void
}

export const useCustomerPurchaseOrderForm = ({
  companyId,
  shipToLocationId,
  onClose,
  onDuplicateSelect,
  onSubmit,
}: UseCustomerPurchaseOrderFormProps): UseCustomerPurchaseOrderFormReturn => {
  const [isDuplicate, setIsDuplicate] = useState(false)
  const [duplicateCpo, setDuplicateCpo] = useState<CustomerPurchaseOrder | undefined>(undefined)
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const methods = useForm<CustomerPurchaseOrderFormValues>({
    defaultValues: { cpoNumber: "" },
    resolver: zodResolver(customerPurchaseOrderFormSchema),
    mode: "onChange",
  })

  const {
    formState: { isValid, isDirty, errors, isSubmitting },
  } = methods

  const { createCustomerPurchaseOrder, isCreating } = useCreateCustomerPurchaseOrder()

  const canSubmit = isValid && isDirty && !Object.keys(errors).length && !isSubmitting && !isDuplicate

  const doCreate = async (confirmDuplicate: boolean) => {
    const formValues = methods.getValues()
    const input = toCustomerPurchaseOrderCreateInput(formValues, companyId, shipToLocationId, { confirmDuplicate })

    const result = await createCustomerPurchaseOrder(input)

    if (!result.success) {
      if (result.isDuplicate) {
        setIsDuplicate(true)
        setDuplicateCpo(result.duplicateCpo)
      }
      return
    }

    setIsDuplicate(false)
    setDuplicateCpo(undefined)
    onSubmit()
    onClose()
  }

  const handleSubmit = methods.handleSubmit(async () => {
    await doCreate(false)
  })

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }

  const handleCreateNew = async () => {
    await doCreate(true)
  }

  const handleViewEditExisting = () => {
    const cpoNumber = methods.getValues("cpoNumber")
    onDuplicateSelect(cpoNumber)
    onClose()
  }

  return {
    methods,
    handleSubmit,
    handleCancel,
    canSubmit,
    isDuplicate,
    duplicateCpo,
    handleCreateNew,
    handleViewEditExisting,
    isCreating,
    isCancelConfirmationOpen,
    setIsCancelConfirmationOpen,
  }
}
